import { analytics } from '@play-co/gcinstant';
import { now } from 'src/StateObserver';
import { Feature } from 'src/replicant/analytics';
import * as Tutorial from 'src/replicant/tutorial';
import { track } from 'src/lib/analytics/utils';
import { trackAppsFlyerTutorialCompletion } from 'src/lib/gcinstant';

const tutorialSteps = {
  blockTipAndTos: {
    stepIndex: 0,
    stepName: 'block_tip_and_tos',
  },
  movesTip: {
    stepIndex: 1,
    stepName: 'moves_tip',
  },
  firstPuzzle: {
    stepIndex: 2,
    stepName: 'first_puzzle',
  },
  openBox: {
    stepIndex: 3,
    stepName: 'open_box',
  },
  basicGameplay: {
    stepIndex: 4,
    stepName: 'basic_gameplay',
  },
  foodCare: {
    stepIndex: 5,
    stepName: 'food_care',
  },
  toyCare: {
    stepIndex: 6,
    stepName: 'toy_care',
  },
};

type TutorialStep = keyof typeof tutorialSteps;
type TutorialStepProps = {
  stepIndex: number;
  stepName: string;
};

const stepTimestamps: Partial<Record<TutorialStep, number>> = {};

/**
 * Retrieves the properties of a specific tutorial step based on the current tutorial version.
 *
 * @param step - The identifier of the tutorial step to retrieve properties for
 * @returns An object containing the properties of the specified tutorial step
 */
export function puzzleCoinsGetStepProps(step: TutorialStep): TutorialStepProps {
  // Control retains its existing step names and indices.
  return tutorialSteps[step];
}

/**
 * Tracks the start of a specific tutorial step and records its timestamp.
 *
 * @param step - The current tutorial step being started
 *
 * @remarks
 * This function performs two key actions:
 * 1. Records the start time of the tutorial step in the `stepTimestamps` record
 * 2. Sends an analytics event with details about the tutorial step
 *
 * @see puzzleCoinsGetStepProps for step property retrieval
 */
export function puzzleCoinsTrackStepStart(step: TutorialStep) {
  // Store step start time
  stepTimestamps[step] = now();

  analytics.game.tutorial.TutorialStepStart({
    ...puzzleCoinsGetStepProps(step),
    feature: Feature.BPOINTS_FROM_PUZZLE._,
    tutorialKey: Tutorial.ruleset.version,
  });
}

/**
 * Tracks the completion of a specific tutorial step and sends analytics data.
 *
 * @param step - The tutorial step being completed
 * @remarks
 * Calculates the elapsed time for the step and reports detailed analytics about the step's completion.
 * Uses the global `stepTimestamps` to determine the start time and calculates duration in seconds.
 *
 * @returns Sends an analytics event with step details, elapsed time, and tutorial version
 */
export function puzzleCoinsTrackStepFinish(step: TutorialStep) {
  // Calculate step elapsed time in seconds for this step
  const startTime = stepTimestamps[step] ?? now();
  const elapsedTime = now() - startTime;
  const stepElapsedSeconds = elapsedTime / 1000;

  analytics.game.tutorial.TutorialStepFinish({
    ...puzzleCoinsGetStepProps(step),
    feature: Feature.BPOINTS_FROM_PUZZLE._,
    tutorialKey: Tutorial.ruleset.version,
    stepElapsedSeconds,
  });
}

/**
 * Tracks the start of a tutorial for puzzle coins.
 *
 * @remarks
 * Sends an analytics event to record the beginning of the tutorial,
 * capturing the specific feature and tutorial version.
 */
export function puzzleCoinsTrackTutorialStart() {
  analytics.game.tutorial.TutorialStart({
    feature: Feature.BPOINTS_FROM_PUZZLE._,
    $subFeature: Feature.BPOINTS_FROM_PUZZLE.TUTORIAL,
    tutorialKey: Tutorial.ruleset.version,
  });
}

/**
 * Tracks the completion of the puzzle coins tutorial.
 *
 * @param autoComplete - Indicates whether the tutorial was completed automatically. Defaults to false.
 * @remarks
 * Sends an analytics event to record the tutorial finish, including the feature, subfeature,
 * completion method, and tutorial version.
 */
export function puzzleCoinsTrackTutorialFinish(autoComplete = false) {
  analytics.game.tutorial.TutorialFinish({
    feature: Feature.BPOINTS_FROM_PUZZLE._,
    $subFeature: Feature.BPOINTS_FROM_PUZZLE.TUTORIAL,
    autoComplete,
    tutorialKey: Tutorial.ruleset.version,
  });

  trackAppsFlyerTutorialCompletion({
    success: true,
    tutorialId: Tutorial.ruleset.version,
    content: Feature.BPOINTS_FROM_PUZZLE.TUTORIAL,
  });
}

/**
 * Tracks the user's acceptance of the Terms of Service (ToS) for Puzzle Coins.
 *
 * @remarks
 * Sends an analytics event to record when a user accepts the Terms of Service,
 * specifically for the Puzzle Coins feature. This helps track user onboarding
 * and consent in the application.
 *
 * @returns {void}
 */
export function puzzleCoinsTrackAcceptToS() {
  track('AcceptToS', {
    feature: Feature.BPOINTS_FROM_PUZZLE._,
    $subFeature: Feature.BPOINTS_FROM_PUZZLE.TOS_ACCEPT,
  });
}
